import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  DASHBOARD,
  getDashboardTintedCardStyles,
} from "../dashboard/dashboardTheme";
import type { NetworkSummaryData } from "../../../services/networkDashboard";
import {
  formatNetworkCurrency,
  formatNetworkRating,
  formatOccupancy,
} from "../../../services/networkDashboardFormat";
import { NetworkVbpImpactInfoTooltip } from "../dashboard/dashboardInfoTooltips";

interface Props {
  summary: NetworkSummaryData | null;
  loading: boolean;
}

const STAT_STRIP_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(5, minmax(0, 1fr))",
  },
  gap: 1.75,
  mb: 2.5,
  width: "100%",
} as const;

function StatCard({
  label,
  value,
  context,
  valueColor,
  infoTooltip,
}: {
  label: string;
  value: React.ReactNode;
  context?: React.ReactNode;
  valueColor?: string;
  infoTooltip?: React.ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        ...getDashboardTintedCardStyles(theme),
        py: 1.75,
        px: 2,
        width: "100%",
        minWidth: 0,
        height: "100%",
        minHeight: 106,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        borderRadius: "10px",
      })}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
        <Typography
          sx={{
            fontSize: "11.5px",
            color: DASHBOARD.text,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 600,
            lineHeight: 1.3,
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
        {infoTooltip}
      </Box>
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 700,
          color: valueColor ?? DASHBOARD.accent,
          mt: 0.75,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: "11.5px",
          color: DASHBOARD.textMuted,
          mt: 0.5,
          minHeight: "2.75em",
          lineHeight: 1.35,
        }}
      >
        {context ?? "\u00a0"}
      </Typography>
    </Box>
  );
}

export default function NetworkHeaderStrip({ summary, loading }: Props) {
  if (loading) {
    return (
      <Box sx={STAT_STRIP_GRID_SX}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={106} sx={{ width: "100%", minWidth: 0 }} />
        ))}
      </Box>
    );
  }

  if (!summary) return null;

  const snapshotCount = summary.snapshot_ccn_count ?? summary.facility_count;
  const portfolioCount = summary.portfolio_ccn_count;
  const facilityValue =
    portfolioCount != null && portfolioCount > snapshotCount
      ? `${snapshotCount} of ${portfolioCount}`
      : snapshotCount;
  const bedsCtx =
    snapshotCount > 0
      ? `${summary.total_beds.toLocaleString()} beds - ${formatOccupancy(summary.occupancy_rate)}`
      : portfolioCount != null && portfolioCount > snapshotCount
        ? `${portfolioCount} facilities in portfolio · no CMS snapshot for this period`
        : `${summary.total_beds.toLocaleString()} beds - ${formatOccupancy(summary.occupancy_rate)}`;
  const ratingDelta =
    summary.network_avg_overall_rating != null && summary.state_avg_overall_rating != null
      ? summary.network_avg_overall_rating - summary.state_avg_overall_rating
      : null;
  const starCtx =
    summary.state_avg_overall_rating != null ? (
      <>
        State avg{" "}
        <Box component="strong" sx={{ fontWeight: 700, color: DASHBOARD.text }}>
          {formatNetworkRating(summary.state_avg_overall_rating)}
        </Box>
        {ratingDelta != null ? (
          <>
            {" · "}
            <Box
              component="span"
              sx={{
                color:
                  ratingDelta < 0 ? "#b85c5c" : ratingDelta > 0 ? "#5d8f7a" : DASHBOARD.textMuted,
              }}
            >
              {ratingDelta > 0 ? "+" : ratingDelta < 0 ? "−" : ""}
              {Math.abs(ratingDelta).toFixed(1)}
            </Box>
          </>
        ) : null}
      </>
    ) : null;

  return (
    <Box sx={STAT_STRIP_GRID_SX}>
      <StatCard label="Total facilities" value={facilityValue} context={bedsCtx} />
      <StatCard
        label="36-mo penalty exposure"
        value={formatNetworkCurrency(summary.penalty_exposure_36mo)}
        valueColor="#b85c5c"
        context={
          summary.penalty_exposure_delta_vs_prior_12mo != null ? (
            <>
              <Box
                component="span"
                sx={{
                  color:
                    summary.penalty_exposure_delta_vs_prior_12mo > 0
                      ? "#b85c5c"
                      : summary.penalty_exposure_delta_vs_prior_12mo < 0
                        ? "#5d8f7a"
                        : DASHBOARD.textMuted,
                }}
              >
                {summary.penalty_exposure_delta_vs_prior_12mo > 0
                  ? "↑"
                  : summary.penalty_exposure_delta_vs_prior_12mo < 0
                    ? "↓"
                    : ""}{" "}
                {formatNetworkCurrency(
                  Math.abs(summary.penalty_exposure_delta_vs_prior_12mo),
                )}
              </Box>
              {" vs prior 12mo"}
            </>
          ) : undefined
        }
      />
      <StatCard
        label="SFF candidates"
        value={summary.sff_count}
        valueColor={summary.sff_count > 0 ? "#b85c5c" : DASHBOARD.accent}
        context={
          summary.sff_facilities?.length
            ? summary.sff_facilities
                .slice(0, 3)
                .map((f) => f.provider_name || f.ccn)
                .join(" - ")
            : undefined
        }
      />
      <StatCard
        label="Network avg overall ★"
        value={
          summary.network_avg_overall_rating != null
            ? formatNetworkRating(summary.network_avg_overall_rating)
            : "—"
        }
        context={starCtx}
      />
      <StatCard
        label="FY24 VBP impact"
        infoTooltip={<NetworkVbpImpactInfoTooltip />}
        value={formatNetworkCurrency(summary.vbp_estimated_impact_fy24)}
        valueColor={
          summary.vbp_estimated_impact_fy24 != null && summary.vbp_estimated_impact_fy24 < 0
            ? "#b85c5c"
            : DASHBOARD.accent
        }
        context={
          summary.vbp_facilities_with_reduction
            ? `${summary.vbp_facilities_with_reduction} facilities multiplier < 1.0`
            : undefined
        }
      />
    </Box>
  );
}
